import { GridApi } from '@ag-grid-community/core';
import { inject, Injectable, signal } from '@angular/core';
import { findIndex } from 'lodash';
import { firstValueFrom } from 'rxjs';

import { GqlService, listBudgetAttributesQuery } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { SortUtils } from '@shared/utils/sort.utils';

export interface ExistingAttribute {
  id: string;
  items: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ExistingAttributesService {
  private gqlService = inject(GqlService);
  private overlayService = inject(OverlayService);

  data = signal<listBudgetAttributesQuery[] | null>(null);
  existingAttributes = signal<ExistingAttribute[]>([]);

  async fetchAttributes(): Promise<void> {
    const { data, errors, success } = await firstValueFrom(this.gqlService.listBudgetAttributes$());

    if (data && success) {
      this.data.set(data);
    } else {
      this.overlayService.error(errors);
    }
  }

  initAttributes = (gridApi: GridApi): void => {
    const attributes = (this.data() || []).map((budgetAttribute) => ({
      id:
        gridApi
          .getAllDisplayedColumns()
          .find((col) => col.getColDef().headerName === budgetAttribute.attribute_name)
          ?.getColDef().field || '',
      items: SortUtils.sortArrayGivenSpecialChars([...budgetAttribute.attribute_values]) || [],
    }));

    this.existingAttributes.set(attributes);
  };

  updateList(id: string, items: string[]): void {
    const updated = this.existingAttributes();
    const index = findIndex(this.existingAttributes(), (attr) => attr.id === id);

    updated.splice(index, 1, {
      ...this.existingAttributes()[index],
      items: SortUtils.sortArrayGivenSpecialChars([...items]),
    });
    this.existingAttributes.set([...updated]);
  }
}
